import Header from '@/components/Header';
import { Spinner } from '@heroui/spinner';
import { Modal, ModalContent, ModalBody } from '@heroui/react';
import useIsMobile from '@/hooks/useIsMobile';
import { useState, useEffect } from 'react';
import MyEventsTab from '@/components/MyEventsTab';
import { CreateEventSkeleton } from '@/screens/CreateEventNew';
import MyTicketsTab from '@/components/MyTicketsTab';
import MyPaymentsTab from '@/components/MyPaymentsTab';
import NotificationsTab from '@/components/NotificationsTab';
import AccountTab from '@/components/AccountTab';
import SettingsTab from '@/components/SettingsTab';
import OrganizerTab from '@/components/OrganizerTab';
import MessagingTab from '@/components/MessagingTab';
import ContactListTab from '@/components/ContactListTab';
import MembersTab from '@/components/MembersTab';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, AuthOnly } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { useUserRoles } from '@/utils/roleUtils';
import VenueManagementScreen from './VenueManagementScreen';
import PayoutManagement from '@/components/PayoutManagement';
import { IconBriefcase, IconCalendarEvent, IconCreditCard, IconHome, IconLogout, IconMapPin, IconSpeakerphone, IconUsers } from '@tabler/icons-react';
import CreateOrganizationForm from '@/components/CreateOrganizationForm';
import Footer from '@/components/Footer';
import { pageBg, card, btnGhost } from '@/components/ui/design';

const getSidebarItems = (isOrganizer: boolean, activeIndex: number, isUsher: boolean = false) => {
    const allItems = [
        {
            label: 'Events',
            icon: <IconCalendarEvent size={24} stroke={2} />

        },
        {
            label: 'My Page',
            icon: <IconHome size={24} stroke={2} />

        },
        {
            label: 'Banking',
            icon: <IconCreditCard size={24} stroke={2} />

        },
        {
            label: 'Marketing',
            icon: <IconSpeakerphone size={24} stroke={2} />

        },
        {
            label: 'Venues',
            icon: <IconMapPin size={24} stroke={2} />
        },
        {
            label: 'Members',
            icon: <IconUsers size={24} stroke={2} />

        }
    ];

    // If user is usher only, only show Events tab
    if (isUsher) {
        return [allItems[0]];
    }

    return allItems;
};


const tabOptions = [
    'events', 'organization', 'payments', 'contacts', 'venues', 'members'
] as const;
type TabType = typeof tabOptions[number];


export default function MyOrganizations() {
    const { tab: urlTab, eid, activeTab } = useParams<{ tab?: string; eid?: string; activeTab?: string }>();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { selectedOrganizerId, userOwnOrganizer, accessibleOrganizers, isLoading: isLoadingOrgContext } = useOrganization();
    const { data: userRolesData, isLoading: isLoadingRoles } = useUserRoles();

    const isOrganizer = !!user?.organizer;
    const userRoles = userRolesData?.roles || [];
    const ownedOrganizerIds = userRolesData?.ownedOrganizerIds || [];

    // Whether the user has access to at least one organization (owned or via authorization email).
    const isCheckingAccess = isLoadingRoles || isLoadingOrgContext;
    const hasAnyOrganization = !!userOwnOrganizer || !!(accessibleOrganizers?.data?.length);

    // Check if user is usher for the selected organization
    // If user owns the organization, they are not a usher
    const isUsher = selectedOrganizerId
        ? (() => {
            // Normalize IDs for comparison
            const selectedId = selectedOrganizerId.toString();
            const isOwner = ownedOrganizerIds.some(id => {
                const normalizedId = id?.toString() || id;
                return normalizedId === selectedId;
            });

            // If user owns the organization, they are not a usher
            if (isOwner) {
                return false;
            }

            // Check if user's role for this organization is 'usher'
            // Normalize organizerId in userRoles for comparison with selectedOrganizerId
            const matchingRole = userRoles.find(r => {
                const roleOrgId = r.organizerId?.toString() || r.organizerId;
                return roleOrgId === selectedId;
            });

            return matchingRole?.role === 'usher';
        })()
        : false;

    // Initialize from the URL immediately so a hard refresh doesn't flash the wrong tab
    // before the roles/usher check below finishes and corrects it.
    const [tab, setTab] = useState<TabType>(() => (
        urlTab && tabOptions.includes(urlTab as TabType) ? urlTab as TabType : 'events'
    ));
    const [refundIdx, setRefundIdx] = useState<number | null>(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    function handleRefundClose() {
        setRefundIdx(null);
    }

    function handleLogoutClose() {
        setShowLogoutModal(false);
    }

    function handleLogoutConfirm() {
        logout();
        setShowLogoutModal(false);
    }

    // Sync tab state with URL on mount and when URL changes
    useEffect(() => {
        // Don't process tab changes while roles are loading
        if (isLoadingRoles) {
            return;
        }

        const validTab = urlTab && tabOptions.includes(urlTab as TabType) ? urlTab as TabType : 'events';

        // If user is usher only, redirect to events tab if trying to access other tabs
        if (isUsher && validTab !== 'events') {
            navigate('/organizations/events', { replace: true });
            setTab('events');
            return;
        }

        // If user is not an organizer and trying to access messaging, redirect to tickets
        if (validTab !== tab) {
            setTab(validTab);
        }
    }, [urlTab, tab, isUsher, navigate, isLoadingRoles]);

    // Update URL when tab changes
    const handleTabChange = (newTab: TabType) => {
        // Prevent ushers from accessing tabs other than events
        if (isUsher && newTab !== 'events') {
            return;
        }
        setTab(newTab);
        navigate(`/organizations/${newTab}`, { replace: true });
    };

    // Close mobile menu on tab change
    // Handle logout
    const handleLogout = () => {
        setShowLogoutModal(true);
    };

    // Sidebar active logic
    const sidebarActiveIdx = tab === 'events' ? 0 : tab === 'organization' ? 1 : tab === 'payments' ? 2 : tab === 'contacts' ? 3 : tab === 'venues' ? 4 : tab === 'members' ? 5 : 0;

    const handleNavItemClick = (idx: number) => {
        if (!hasAnyOrganization) {
            handleTabChange('organization');
            return;
        }
        if (idx === 0) handleTabChange('events');
        else if (idx === 1) handleTabChange('organization');
        else if (idx === 2) handleTabChange('payments');
        else if (idx === 3) handleTabChange('contacts');
        else if (idx === 4) handleTabChange('venues');
        else if (idx === 5) handleTabChange('members');
    };

    // A user with no organization yet only has a single meaningful destination: create one.
    // Keep the top nav visible while access is still being checked, rather than blanking it out.
    const navItems = isCheckingAccess
        ? getSidebarItems(true, sidebarActiveIdx, isUsher)
        : !hasAnyOrganization
            ? [{ label: 'Organization', icon: <IconBriefcase size={24} stroke={2} /> }]
            : getSidebarItems(true, sidebarActiveIdx, isUsher);
    const isMobile = useIsMobile();

    return (
        <AuthOnly>
            <Header
                organizerNav={{
                    email: user?.email,
                    items: navItems.map((item: any, idx: number) => ({
                        key: item.label,
                        label: item.label,
                        icon: item.icon,
                        active: isCheckingAccess ? idx === sidebarActiveIdx : !hasAnyOrganization ? true : idx === sidebarActiveIdx,
                        onClick: () => handleNavItemClick(idx),
                    })),
                    hideMobileTopBar: tab === 'events' && !!eid,
                }}
            />
            <div className={`${pageBg} w-full`}>
            <div className={`w-full pb-[calc(49px+env(safe-area-inset-bottom)+1.5rem)] sm:pb-0 md:pb-28 min-h-[70vh] flex flex-col gap-5 ${tab === 'events' && eid ? 'pt-0 lg:pt-0' : 'pt-20 md:pt-10 px-4 xl:px-0 max-w-[1200px] mx-auto'}`} style={{ scrollBehavior: 'smooth' }}>
                {/* Main Content */}
                <main className="flex-1 w-full min-h-0" >
                    <div className={`min-h-full ${activeTab === 'Tickets' ? '' : ''}`} >
                        {/* Tabs */}
                        {isCheckingAccess ? (
                            tab === 'events' && eid === 'create' ? (
                                <CreateEventSkeleton />
                            ) : (
                                <div className="flex items-center justify-center py-24">
                                    <Spinner color="primary" size="lg" />
                                </div>
                            )
                        ) : !hasAnyOrganization ? (
                            <div className="w-full flex justify-center py-4 sm:py-8">
                                <div className={`w-full max-w-3xl ${card} p-4 sm:p-8`}>
                                    <CreateOrganizationForm />
                                </div>
                            </div>
                        ) : (
                            <>
                                {tab === 'events' && (
                                    <MyEventsTab eid={eid} activeTab={activeTab} />
                                )}
                                {!isUsher && tab === 'organization' && (
                                    <OrganizerTab />
                                )}
                                {!isUsher && tab === 'payments' && (
                                    <PayoutManagement />
                                )}
                                {!isUsher && tab === 'contacts' && (
                                    <ContactListTab />
                                )}
                                {!isUsher && tab === 'venues' && (
                                    <VenueManagementScreen />
                                )}
                                {!isUsher && tab === 'members' && (
                                    <MembersTab organizerId={selectedOrganizerId} />
                                )}
                            </>
                        )}
                        {/* Refund Modal (keep for now, but not used in tickets tab) */}
                        <Modal
                            isOpen={refundIdx !== null}
                            onClose={handleRefundClose}
                            size={isMobile ? 'full' : 'sm'}
                            classNames={{
                                base: 'bg-white dark:bg-nightElevated rounded-none md:rounded-[28px] shadow-none md:shadow-2xl md:ring-1 md:ring-black/[0.06] dark:md:ring-white/10 h-full md:h-auto',
                                wrapper: 'p-0 md:p-4 z-[100000]',
                            }}
                        >
                            <ModalContent>
                                <ModalBody className="p-8 flex flex-col items-center justify-center text-center">
                                    <div className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white mb-2">Refund payment:</div>
                                    <div className="text-base leading-relaxed text-grey40 dark:text-nightMuted mb-8">
                                        You're about to refund order <span className="font-bold">#</span>.
                                    </div>
                                    <div className="flex gap-3 w-full justify-center">
                                        <button className={`${btnGhost} flex-1`} onClick={handleRefundClose}>Cancel</button>
                                        <button className="inline-flex h-12 flex-1 items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-white transition-colors hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70">Refund</button>
                                    </div>
                                </ModalBody>
                            </ModalContent>
                        </Modal>

                        {/* Logout Confirmation Modal */}
                        <Modal
                            isOpen={showLogoutModal}
                            onClose={handleLogoutClose}
                            size={isMobile ? 'full' : 'sm'}
                            classNames={{
                                base: 'bg-white dark:bg-nightElevated rounded-none md:rounded-[28px] shadow-none md:shadow-2xl md:ring-1 md:ring-black/[0.06] dark:md:ring-white/10 h-full md:h-auto',
                                wrapper: 'p-0 md:p-4 z-[100000]',
                            }}
                        >
                            <ModalContent>
                                <ModalBody className="p-8 flex flex-col items-center justify-center text-center">
                                    <div className="flex items-center justify-center mb-4">
                                        <IconLogout size={24} stroke={1.5} />
                                    </div>
                                    <div className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white mb-2">Logout</div>
                                    <div className="text-base leading-relaxed text-grey40 dark:text-nightMuted mb-8">
                                        Are you sure you want to logout? You'll need to sign in again to access your account.
                                    </div>
                                    <div className="flex gap-3 w-full justify-center">
                                        <button className={`${btnGhost} flex-1`} onClick={handleLogoutClose}>Cancel</button>
                                        <button className="inline-flex h-12 flex-1 items-center justify-center rounded-full bg-red-500 px-6 text-sm font-semibold text-white transition-colors hover:bg-red-600" onClick={handleLogoutConfirm}>Logout</button>
                                    </div>
                                </ModalBody>
                            </ModalContent>
                        </Modal>
                    </div>
                </main>
            </div>
            </div>
            <Footer />
        </AuthOnly>
    );
} 